// ABOUTME: Parent management page for chore lists - allows creating, editing, and managing chore lists for children
// ABOUTME: Supports daily/weekly/monthly repeating chores with optional heart-earning requirement

import React, { useState, useEffect, useCallback, useRef, useMemo } from 'react';
import axios from 'axios';
import {
  Box,
  Container,
  Typography,
  Button,
  Card,
  CardContent,
  Chip,
  IconButton,
  LinearProgress,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  FormControlLabel,
  FormLabel,
  Switch,
  List,
  ListItem,
  ListItemText,
  ListItemIcon,
  Checkbox,
  Alert,
  CircularProgress,
  Divider,
  Tooltip,
  Accordion,
  AccordionSummary,
  AccordionDetails,
  Snackbar,
} from '@mui/material';
import {
  Add as AddIcon,
  Edit as EditIcon,
  Delete as DeleteIcon,
  ContentCopy as CopyIcon,
  ExpandMore as ExpandMoreIcon,
  Favorite as HeartIcon,
  CheckCircle as CheckCircleIcon,
  RadioButtonUnchecked as UncheckedIcon,
  DragIndicator as DragIcon,
  EventBusy as SkipIcon,
  Replay as RestoreIcon,
  Archive as ArchiveIcon,
} from '@mui/icons-material';
import {
  DndContext,
  closestCenter,
  KeyboardSensor,
  PointerSensor,
  useSensor,
  useSensors,
  DragEndEvent,
} from '@dnd-kit/core';
import {
  arrayMove,
  SortableContext,
  sortableKeyboardCoordinates,
  useSortable,
  verticalListSortingStrategy,
} from '@dnd-kit/sortable';
import { CSS } from '@dnd-kit/utilities';
import { alpha, useTheme } from '@mui/material/styles';
import naluV1Theme from '../theme/theme.nalu-v1';
import { celebrationGold } from '../theme/tokens';
import { useAuth } from '../contexts/AuthContext';
import { useSignalR } from '../contexts/SignalRContext';
import { useTimezone } from '../contexts/TimezoneContext';
import { familyAPI, choresAPI } from '../services/api';
import SharedAppBar from './SharedAppBar';
import { useCelebration } from '../hooks/useCelebration';
import { getTodayInFamilyTimezone } from '../utils/dateUtils';
import { ChoreListType, ChoreListStatus } from '../types/api';
import type {
  Family,
  Child,
  ChoreList,
  CreateChoreListRequest,
  UpdateChoreListRequest,
  RepeatFrequency,
} from '../types/api';

interface FormItem {
  id?: number;
  tempId: string; // Stable ID for drag and drop
  title: string;
  sortOrder: number;
}

interface ChoreListFormData {
  title: string;
  notes: string;
  childId: number;
  startDate: string;
  repeatFrequency: RepeatFrequency;
  requiredForHeart: boolean;
  requiresApproval: boolean;
  /** Bitmask of active days (1–127). Only used for Daily lists. */
  daysOfWeek: number;
  items: FormItem[];
}

// Days of week labels and their bit positions (matches DayOfWeek enum: Sun=0→bit1, Mon=1→bit2, etc.)
const DAYS_OF_WEEK = [
  { label: 'Sun', bit: 1 << 0 },
  { label: 'Mon', bit: 1 << 1 },
  { label: 'Tue', bit: 1 << 2 },
  { label: 'Wed', bit: 1 << 3 },
  { label: 'Thu', bit: 1 << 4 },
  { label: 'Fri', bit: 1 << 5 },
  { label: 'Sat', bit: 1 << 6 },
] as const;

// Generate unique ID for new items
let itemIdCounter = 0;
const generateTempId = () => `temp-${Date.now()}-${itemIdCounter++}`;

// Normalize repeat frequency from backend (may be number or string)
const normalizeRepeatFrequency = (value: RepeatFrequency | number): RepeatFrequency => {
  if (typeof value === 'number') {
    switch (value) {
      case 0: return 'Daily';
      case 1: return 'Weekly';
      case 2: return 'Monthly';
      default: return 'Daily';
    }
  }
  return value;
};

const emptyFormData: ChoreListFormData = {
  title: '',
  notes: '',
  childId: 0,
  startDate: '',
  repeatFrequency: 'Daily',
  requiredForHeart: false,
  requiresApproval: true,
  daysOfWeek: 127,
  items: [],
};

// Sortable item component for drag and drop
interface SortableItemProps {
  id: string;
  title: string;
  onRemove: () => void;
}

const SortableItem: React.FC<SortableItemProps> = ({ id, title, onRemove }) => {
  const theme = useTheme();
  // ThemeProvider's resolved theme and this module's naluV1Theme import different
  // instances under Vite HMR, so compare a stable field rather than object identity.
  const isNaluV1Theme = theme.logoSrc === naluV1Theme.logoSrc;

  const {
    attributes,
    listeners,
    setNodeRef,
    transform,
    transition,
    isDragging,
  } = useSortable({ id });

  const style = {
    transform: CSS.Transform.toString(transform),
    transition,
    opacity: isDragging ? 0.5 : 1,
  };

  return (
    <ListItem
      ref={setNodeRef}
      style={style}
      dense
      sx={{
        py: 0.5,
        bgcolor: isDragging ? 'action.selected' : 'transparent',
        '&:hover': { bgcolor: 'action.hover' },
        borderRadius: isNaluV1Theme ? '8px' : 1,
        mb: 0.5,
        border: '1px solid',
        borderColor: 'divider',
      }}
    >
      <Box
        {...attributes}
        {...listeners}
        sx={{
          display: 'flex',
          alignItems: 'center',
          cursor: 'grab',
          mr: 1,
          color: 'text.secondary',
          '&:active': { cursor: 'grabbing' },
        }}
      >
        <DragIcon fontSize="small" />
      </Box>
      <ListItemIcon sx={{ minWidth: 36 }}>
        <Checkbox
          edge="start"
          checked={false}
          tabIndex={-1}
          disableRipple
          disabled
          sx={{ color: 'action.disabled' }}
        />
      </ListItemIcon>
      <ListItemText primary={title} />
      <IconButton edge="end" size="small" onClick={onRemove}>
        <DeleteIcon fontSize="small" />
      </IconButton>
    </ListItem>
  );
};

const getRepeatLabel = (frequency: RepeatFrequency | number): string => {
  if (typeof frequency === 'number') {
    return normalizeRepeatFrequency(frequency);
  }
  switch (frequency) {
    case 'Daily':
      return 'Daily';
    case 'Weekly':
      return 'Weekly';
    case 'Monthly':
      return 'Monthly';
    default:
      return frequency;
  }
};

/** True if the chore list has been retired (EndDate is set and in the past or today) */
const isRetired = (choreList: ChoreList, today: string): boolean => {
  return !!choreList.endDate && choreList.endDate <= today;
};

interface SortableChoreListCardProps {
  choreList: ChoreList;
  highlightedListId: number | null;
  todayDayBit: number;
  onEdit: (choreList: ChoreList) => void;
  onCopy: (choreList: ChoreList) => void;
  onToggleItem: (choreList: ChoreList, itemId: number, isCompleted: boolean) => void;
  onSkip: (choreList: ChoreList) => void;
  onUnskip: (choreList: ChoreList) => void;
  onRetire: (choreList: ChoreList) => void;
}

const SortableChoreListCard: React.FC<SortableChoreListCardProps> = ({
  choreList,
  highlightedListId,
  todayDayBit,
  onEdit,
  onCopy,
  onToggleItem,
  onSkip,
  onUnskip,
  onRetire,
}) => {
  const theme = useTheme();
  // ThemeProvider's resolved theme and this module's naluV1Theme import different
  // instances under Vite HMR, so compare a stable field rather than object identity.
  const isNaluV1Theme = theme.logoSrc === naluV1Theme.logoSrc;

  const {
    attributes,
    listeners,
    setNodeRef,
    transform,
    transition,
    isDragging,
  } = useSortable({ id: choreList.id });

  const style = {
    transform: CSS.Transform.toString(transform),
    transition,
    opacity: isDragging ? 0.5 : 1,
  };

  const isApproved = choreList.status === ChoreListStatus.Finalized &&
    (choreList.type === ChoreListType.MakeItRight || choreList.requiresApproval);

  return (
    <Box ref={setNodeRef} style={style}>
      <Card
        variant="outlined"
        sx={{
          transition: 'box-shadow 0.3s ease-in-out',
          ...(highlightedListId === choreList.id && {
            boxShadow: `0 0 20px 5px ${alpha(celebrationGold, 0.6)}`,
            animation: 'pulse 1s ease-in-out 2',
            '@keyframes pulse': {
              '0%, 100%': { boxShadow: `0 0 20px 5px ${alpha(celebrationGold, 0.6)}` },
              '50%': { boxShadow: `0 0 30px 10px ${alpha(celebrationGold, 0.8)}` },
            },
          })
        }}
      >
        <CardContent>
          <Box display="flex" justifyContent="space-between" alignItems="flex-start">
            <Box display="flex" alignItems="flex-start" gap={1}>
              <Tooltip title="Drag to reorder">
                <Box
                  {...attributes}
                  {...listeners}
                  sx={{
                    display: 'flex',
                    alignItems: 'center',
                    cursor: 'grab',
                    mt: 0.5,
                    color: 'text.secondary',
                    '&:active': { cursor: 'grabbing' },
                  }}
                  aria-label="Drag to reorder"
                >
                  <DragIcon fontSize="small" />
                </Box>
              </Tooltip>
              <Box>
                <Box display="flex" alignItems="center" gap={1}>
                  <Typography variant="h6">{choreList.title}</Typography>
                  {choreList.requiredForHeart && (
                    <Tooltip title="Required for daily heart">
                      <Box sx={{ bgcolor: 'white', borderRadius: '50%', width: 24, height: 24, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                        <HeartIcon color="error" fontSize="small" />
                      </Box>
                    </Tooltip>
                  )}
                </Box>
                <Box display="flex" gap={1} mt={0.5}>
                  <Chip
                    label={getRepeatLabel(choreList.repeatFrequency)}
                    size="small"
                    color="primary"
                    variant="outlined"
                  />
                  <Chip
                    label={`${choreList.completedItems}/${choreList.totalItems} done`}
                    size="small"
                    color={choreList.isComplete ? 'success' : 'default'}
                  />
                </Box>
              </Box>
            </Box>
            <Box>
              {normalizeRepeatFrequency(choreList.repeatFrequency) === 'Daily' && !choreList.isSkippedToday && (
                <Tooltip title="Skip today">
                  <IconButton size="small" onClick={() => onSkip(choreList)} aria-label="Skip today">
                    <SkipIcon fontSize="small" />
                  </IconButton>
                </Tooltip>
              )}
              <Tooltip title="Edit">
                <IconButton size="small" onClick={() => onEdit(choreList)}>
                  <EditIcon fontSize="small" />
                </IconButton>
              </Tooltip>
              <Tooltip title="Copy">
                <IconButton size="small" onClick={() => onCopy(choreList)}>
                  <CopyIcon fontSize="small" />
                </IconButton>
              </Tooltip>
              {choreList.type !== ChoreListType.MakeItRight && (
                <Tooltip title="Retire this list">
                  <IconButton
                    size="small"
                    onClick={() => onRetire(choreList)}
                    aria-label="Retire responsibility list"
                  >
                    <ArchiveIcon fontSize="small" />
                  </IconButton>
                </Tooltip>
              )}
            </Box>
          </Box>

          {(choreList.isSkippedToday || (normalizeRepeatFrequency(choreList.repeatFrequency) === 'Daily' && (choreList.daysOfWeek & todayDayBit) === 0)) && (
            <Box display="flex" gap={1} mt={1}>
              {choreList.isSkippedToday && (
                <Box display="flex" alignItems="center" gap={0.5}>
                  <Chip
                    icon={<SkipIcon fontSize="small" />}
                    label="Skipped today"
                    size="small"
                    color="warning"
                    variant="outlined"
                  />
                  <Tooltip title="Restore today">
                    <IconButton size="small" onClick={() => onUnskip(choreList)} aria-label="Restore today">
                      <RestoreIcon fontSize="small" />
                    </IconButton>
                  </Tooltip>
                </Box>
              )}
              {normalizeRepeatFrequency(choreList.repeatFrequency) === 'Daily' && (choreList.daysOfWeek & todayDayBit) === 0 && (
                <Chip
                  label="Not scheduled today"
                  size="small"
                  color="default"
                  variant="outlined"
                />
              )}
            </Box>
          )}

          {choreList.notes && (
            <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
              {choreList.notes}
            </Typography>
          )}

          <LinearProgress
            variant="determinate"
            value={
              choreList.totalItems > 0
                ? (choreList.completedItems / choreList.totalItems) * 100
                : 0
            }
            sx={{ mt: 2, mb: 1, height: 8, borderRadius: isNaluV1Theme ? '999px' : 4 }}
            color={choreList.isComplete ? 'success' : 'primary'}
          />

          <Divider sx={{ my: 1 }} />

          <List dense disablePadding>
            {choreList.items.map((item) => (
              <ListItem key={item.id} disablePadding>
                <Checkbox
                  edge="start"
                  checked={item.isCompleted}
                  onChange={isApproved ? undefined : () =>
                    onToggleItem(choreList, item.id, item.isCompleted)
                  }
                  disabled={isApproved}
                  icon={<UncheckedIcon />}
                  checkedIcon={<CheckCircleIcon color="success" />}
                />
                <ListItemText
                  primary={item.title}
                  sx={{
                    textDecoration: item.isCompleted ? 'line-through' : 'none',
                    color: item.isCompleted ? 'text.secondary' : 'text.primary',
                  }}
                />
              </ListItem>
            ))}
          </List>
        </CardContent>
      </Card>
    </Box>
  );
};

interface ChoresPageProps {
  /** When true, renders without SharedAppBar and full-page wrapper */
  embedded?: boolean;
  /** When set, only shows chore lists for this child */
  filterToChildId?: number;
  /** When set, skips family loading and uses this familyId directly */
  familyIdOverride?: number;
}

const ChoresPage: React.FC<ChoresPageProps> = ({ embedded, filterToChildId, familyIdOverride }) => {
  const { user } = useAuth();
  const { on, connectionState } = useSignalR();
  // Track previous connectionState to detect Disconnected→Connected transitions for re-fetch
  const prevConnectionStateRef = useRef<string>(connectionState);
  const { getTodayInFamilyTimezone: getToday } = useTimezone();
  const { celebrate } = useCelebration();

  // Data state
  const [families, setFamilies] = useState<Family[]>([]);
  const [selectedFamily, setSelectedFamily] = useState<Family | null>(null);
  const selectedFamilyRef = useRef<Family | null>(null);
  const [children, setChildren] = useState<Child[]>([]);
  const [choreLists, setChoreLists] = useState<ChoreList[]>([]);

  // UI state
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [expandedChild, setExpandedChild] = useState<number | null>(null);

  // Modal state
  const [modalOpen, setModalOpen] = useState(false);
  const [editingChoreList, setEditingChoreList] = useState<ChoreList | null>(null);
  const [isCopying, setIsCopying] = useState(false); // True when copying an existing list
  const [formData, setFormData] = useState<ChoreListFormData>(emptyFormData);
  const [formErrors, setFormErrors] = useState<Record<string, string>>({});
  const [isSaving, setIsSaving] = useState(false);

  // Delete confirmation


  // Retire confirmation
  const [retireDialogOpen, setRetireDialogOpen] = useState(false);
  const [retireTarget, setRetireTarget] = useState<ChoreList | null>(null);

  // New item input
  const [newItemTitle, setNewItemTitle] = useState('');

  // Celebration state
  const [celebrationMessage, setCelebrationMessage] = useState<string | null>(null);
  const [highlightedListId, setHighlightedListId] = useState<number | null>(null);

  // Skip-today snackbar
  const [skipMessage, setSkipMessage] = useState<string | null>(null);

  // Today's date string in family timezone (YYYY-MM-DD)
  const todayStr = useMemo(() => getToday(), [getToday]);

  // Bitmask for today's day-of-week in family timezone (Sun=1, Mon=2, ..., Sat=64)
  const todayDayBit = useMemo(() => {
    // Parse as noon local to avoid DST edge cases shifting the date
    const dow = new Date(todayStr + 'T12:00:00').getDay();
    return 1 << dow;
  }, [todayStr]);

  // Load families on mount (skip when familyIdOverride is provided)
  useEffect(() => {
    if (user && !familyIdOverride) {
      loadFamilies();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps -- loadFamilies intentionally omitted; it is stable and adding it would cause extra fetches
  }, [user]);

  // When familyIdOverride is provided, bypass family loading and use the given ID directly
  useEffect(() => {
    if (familyIdOverride) {
      const syntheticFamily = { familyId: familyIdOverride, name: '' } as Family;
      setFamilies([syntheticFamily]);
      setSelectedFamily(syntheticFamily);
    }
  }, [familyIdOverride]);

  // When filtering to a specific child, auto-expand that child's section
  useEffect(() => {
    if (filterToChildId) {
      setExpandedChild(filterToChildId);
    }
  }, [filterToChildId]);

  // Helper to safely get child ID (childId or id)
  const getChildId = (child: Child): number => child.childId ?? child.id ?? 0;

  // When filtering to a specific child, only show that child
  const displayChildren = filterToChildId
    ? children.filter((c) => getChildId(c) === filterToChildId)
    : children;

  // Keep ref in sync for SignalR handlers that need current family without stale closure
  useEffect(() => {
    selectedFamilyRef.current = selectedFamily;
  }, [selectedFamily]);

  // Load children and chore lists when family changes
  useEffect(() => {
    if (selectedFamily) {
      loadFamilyData(selectedFamily.familyId);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps -- loadFamilyData intentionally omitted; it is stable and adding it would cause extra fetches
  }, [selectedFamily]);

  // Subscribe to real-time chore events and update chore list state in-place
  useEffect(() => {
    const unsubUpdated = on('ChoreListUpdated', (data: unknown) => {
      const updatedList = data as ChoreList;
      setChoreLists(current => {
        const exists = current.some(list => list.id === updatedList.id);
        if (exists) {
          return current.map(list => list.id === updatedList.id ? updatedList : list);
        }
        return [...current, updatedList];
      });
    });
    const unsubMIR = on('MakeItRightUpdated', async (data: unknown) => {
      const payload = data as { childId: number };
      const family = selectedFamilyRef.current;
      if (!family) return;
      try {
        const refreshed = await choresAPI.getChoreListsForChild(family.familyId, payload.childId);
        setChoreLists(current => [
          ...current.filter(list => list.childId !== payload.childId),
          ...refreshed,
        ]);
      } catch {
        // Silent failure - stale state is acceptable
      }
    });
    const unsubRAR = on('ResetAndReflectUpdated', async (data: unknown) => {
      const payload = data as { childId: number };
      const family = selectedFamilyRef.current;
      if (!family) return;
      try {
        const refreshed = await choresAPI.getChoreListsForChild(family.familyId, payload.childId);
        setChoreLists(current => [
          ...current.filter(list => list.childId !== payload.childId),
          ...refreshed,
        ]);
      } catch {
        // Silent failure - stale state is acceptable
      }
    });
    const unsubOOO = on('OneOnOneTimeUpdated', async (data: unknown) => {
      const payload = data as { childId: number };
      const family = selectedFamilyRef.current;
      if (!family) return;
      try {
        const refreshed = await choresAPI.getChoreListsForChild(family.familyId, payload.childId);
        setChoreLists(current => [
          ...current.filter(list => list.childId !== payload.childId),
          ...refreshed,
        ]);
      } catch {
        // Silent failure - stale state is acceptable
      }
    });
    const unsubPeriodReset = on('PeriodReset', () => {
      const family = selectedFamilyRef.current;
      if (!family) return;
      choresAPI.getChoreListsForFamily(family.familyId)
        .then(lists => setChoreLists(lists))
        .catch(err => console.error('Failed to reload chore lists on PeriodReset:', err));
    });
    return () => {
      unsubUpdated();
      unsubMIR();
      unsubRAR();
      unsubOOO();
      unsubPeriodReset();
    };
  }, [on]);

  // Re-fetch chore lists when SignalR reconnects (Disconnected→Connected transition).
  useEffect(() => {
    if (prevConnectionStateRef.current !== 'Connected' && connectionState === 'Connected') {
      const family = selectedFamilyRef.current;
      if (family) {
        choresAPI.getChoreListsForFamily(family.familyId)
          .then(lists => setChoreLists(lists))
          .catch(err => console.error('Failed to reload chore lists on reconnect:', err));
      }
    }
    prevConnectionStateRef.current = connectionState;
  }, [connectionState]);

  const loadFamilies = async () => {
    try {
      setIsLoading(true);
      setError(null);
      const familiesData = await familyAPI.getFamilies();
      setFamilies(familiesData);
      if (familiesData.length > 0 && !selectedFamily) {
        setSelectedFamily(familiesData[0]);
      }
    } catch (err) {
      console.error('Failed to load families:', err);
      setError('Unable to load family data.');
    } finally {
      setIsLoading(false);
    }
  };

  const loadFamilyData = async (familyId: number) => {
    try {
      setIsLoading(true);
      setError(null);

      const [childrenData, choreListsData] = await Promise.all([
        familyAPI.getChildren(familyId),
        choresAPI.getChoreListsForFamily(familyId),
      ]);

      setChildren(childrenData);
      setChoreLists(choreListsData);

      // Expand first child with chores by default (skip when filterToChildId controls expansion)
      if (!filterToChildId) {
        const getChildIdLocal = (c: Child): number => c.childId ?? c.id ?? 0;
        const firstChildWithChores = childrenData.find((child: Child) =>
          choreListsData.some((cl: ChoreList) => cl.childId === getChildIdLocal(child))
        );
        if (firstChildWithChores) {
          setExpandedChild(getChildIdLocal(firstChildWithChores));
        } else if (childrenData.length > 0) {
          setExpandedChild(getChildIdLocal(childrenData[0]));
        }
      }
    } catch (err) {
      console.error('Failed to load family data:', err);
      setError('Unable to load data.');
    } finally {
      setIsLoading(false);
    }
  };

  const handleOpenCreateModal = (childId?: number) => {
    setEditingChoreList(null);
    setIsCopying(false);
    setFormData({
      ...emptyFormData,
      childId: childId || (children.length > 0 ? (getChildId(children[0])) : 0),
      startDate: getTodayInFamilyTimezone(selectedFamily?.timezone),
    });
    setFormErrors({});
    setNewItemTitle('');
    setModalOpen(true);
  };

  const handleOpenEditModal = (choreList: ChoreList) => {
    setEditingChoreList(choreList);
    setIsCopying(false);
    setFormData({
      title: choreList.title,
      notes: choreList.notes || '',
      childId: choreList.childId,
      startDate: choreList.startDate,
      repeatFrequency: normalizeRepeatFrequency(choreList.repeatFrequency),
      requiredForHeart: choreList.requiredForHeart,
      requiresApproval: choreList.requiresApproval,
      daysOfWeek: choreList.daysOfWeek ?? 127,
      items: choreList.items.map((item) => ({
        id: item.id,
        tempId: generateTempId(), // Generate stable ID for drag and drop
        title: item.title,
        sortOrder: item.sortOrder,
      })),
    });
    setFormErrors({});
    setNewItemTitle('');
    setModalOpen(true);
  };

  const handleOpenCopyModal = (choreList: ChoreList) => {
    setEditingChoreList(null); // Not editing, creating new
    setIsCopying(true);

    // Auto-increment copy name if duplicate exists (Issue #1372)
    const generateUniqueCopyTitle = (originalTitle: string, targetChildId: number): string => {
      const childLists = choreLists.filter(cl =>
        cl.childId === targetChildId &&
        cl.type === ChoreListType.Regular
      );

      let candidateTitle = `${originalTitle} (Copy)`;
      let copyNumber = 2;

      // Keep incrementing until we find a unique name
      while (childLists.some(cl => cl.title.toLowerCase() === candidateTitle.toLowerCase())) {
        candidateTitle = `${originalTitle} (Copy ${copyNumber})`;
        copyNumber++;
      }

      return candidateTitle;
    };

    const uniqueTitle = generateUniqueCopyTitle(choreList.title, choreList.childId);

    setFormData({
      title: uniqueTitle,
      notes: choreList.notes || '',
      childId: choreList.childId, // Default to same child, can be changed
      startDate: getTodayInFamilyTimezone(selectedFamily?.timezone), // Start fresh from today in family timezone
      repeatFrequency: normalizeRepeatFrequency(choreList.repeatFrequency),
      requiredForHeart: choreList.requiredForHeart,
      requiresApproval: choreList.requiresApproval,
      daysOfWeek: choreList.daysOfWeek ?? 127,
      items: choreList.items.map((item) => ({
        // No id - these will be new items
        tempId: generateTempId(),
        title: item.title,
        sortOrder: item.sortOrder,
      })),
    });
    setFormErrors({});
    setNewItemTitle('');
    setModalOpen(true);
  };

  const handleCloseModal = () => {
    setModalOpen(false);
    setEditingChoreList(null);
    setIsCopying(false);
    setFormData(emptyFormData);
    setFormErrors({});
  };

  const validateForm = (): boolean => {
    const errors: Record<string, string> = {};

    if (!formData.title.trim()) {
      errors.title = 'Title is required';
    } else {
      // Check for duplicate name (Issue #1372)
      const normalizedTitle = formData.title.trim().toLowerCase();
      const childLists = choreLists.filter(cl => cl.childId === formData.childId);
      const duplicate = childLists.find(cl =>
        cl.title.toLowerCase() === normalizedTitle &&
        cl.type === ChoreListType.Regular &&
        (!editingChoreList || cl.id !== editingChoreList.id) // Exclude current list when editing
      );

      if (duplicate) {
        const child = children.find(c => getChildId(c) === formData.childId);
        errors.title = `A responsibility list named '${formData.title.trim()}' already exists for ${child?.name || 'this child'}. Please use a different name.`;
      }
    }

    if (!formData.childId) {
      errors.childId = 'Please select a child';
    }
    if (!formData.startDate) {
      errors.startDate = 'Start date is required';
    }
    if (formData.items.length === 0) {
      errors.items = 'Add at least one item';
    }
    if (formData.repeatFrequency === 'Daily' && (formData.daysOfWeek < 1 || formData.daysOfWeek > 127)) {
      errors.daysOfWeek = 'At least one day must be selected';
    }

    setFormErrors(errors);
    return Object.keys(errors).length === 0;
  };

  const handleSave = async () => {
    if (!validateForm() || !selectedFamily) return;

    setIsSaving(true);
    try {
      if (editingChoreList) {
        // Update existing
        const updateRequest: UpdateChoreListRequest = {
          title: formData.title,
          notes: formData.notes || undefined,
          requiredForHeart: formData.repeatFrequency === 'Daily' ? formData.requiredForHeart : false,
          requiresApproval: formData.requiresApproval,
          daysOfWeek: formData.repeatFrequency === 'Daily' ? formData.daysOfWeek : undefined,
          items: formData.items.map((item, index) => ({
            id: item.id,
            title: item.title,
            sortOrder: index,
          })),
        };
        await choresAPI.updateChoreList(editingChoreList.id, updateRequest);
      } else {
        // Create new
        const createRequest: CreateChoreListRequest = {
          childId: formData.childId,
          title: formData.title,
          notes: formData.notes || undefined,
          startDate: formData.startDate,
          repeatFrequency: formData.repeatFrequency,
          requiredForHeart: formData.repeatFrequency === 'Daily' ? formData.requiredForHeart : false,
          requiresApproval: formData.requiresApproval,
          daysOfWeek: formData.repeatFrequency === 'Daily' ? formData.daysOfWeek : undefined,
          items: formData.items.map((item, index) => ({
            title: item.title,
            sortOrder: index,
          })),
        };
        await choresAPI.createChoreList(selectedFamily.familyId, formData.childId, createRequest);
      }

      handleCloseModal();
      loadFamilyData(selectedFamily.familyId);
    } catch (err: unknown) {
      console.error('Failed to save chore list:', err);

      // Check if it's a duplicate name error (Issue #1372)
      const errorMessage = (axios.isAxiosError(err) ? err.response?.data?.message : undefined)
        || (err instanceof Error ? err.message : '')
        || '';
      if (errorMessage.toLowerCase().includes('already exists') || errorMessage.toLowerCase().includes('duplicate')) {
        const child = children.find(c => getChildId(c) === formData.childId);
        setFormErrors({
          title: `A responsibility list named '${formData.title.trim()}' already exists for ${child?.name || 'this child'}. Please use a different name.`
        });
      } else {
        setFormErrors({ submit: 'Failed to save. Please try again.' });
      }
    } finally {
      setIsSaving(false);
    }
  };

  const handleAddItem = () => {
    if (!newItemTitle.trim()) return;

    setFormData({
      ...formData,
      items: [
        ...formData.items,
        { tempId: generateTempId(), title: newItemTitle.trim(), sortOrder: formData.items.length },
      ],
    });
    setNewItemTitle('');
    setFormErrors({ ...formErrors, items: '' });
  };

  const handleRemoveItem = (index: number) => {
    const newItems = formData.items.filter((_, i) => i !== index);
    setFormData({
      ...formData,
      items: newItems.map((item, i) => ({ ...item, sortOrder: i })),
    });
  };

  // Drag and drop sensors
  const sensors = useSensors(
    useSensor(PointerSensor),
    useSensor(KeyboardSensor, {
      coordinateGetter: sortableKeyboardCoordinates,
    })
  );

  const handleDragEnd = (event: DragEndEvent) => {
    const { active, over } = event;

    if (over && active.id !== over.id) {
      const oldIndex = formData.items.findIndex((item) => item.tempId === active.id);
      const newIndex = formData.items.findIndex((item) => item.tempId === over.id);

      const newItems = arrayMove(formData.items, oldIndex, newIndex);
      setFormData({
        ...formData,
        items: newItems.map((item, i) => ({ ...item, sortOrder: i })),
      });
    }
  };

  const handleChoreListDragEnd = async (event: DragEndEvent, childId: number) => {
    const { active, over } = event;
    if (!over || active.id === over.id || !selectedFamily) return;

    const regularLists = choreLists
      .filter(cl => cl.childId === childId && cl.type === ChoreListType.Regular)
      .slice()
      .sort((a, b) => a.sortOrder - b.sortOrder);

    const oldIndex = regularLists.findIndex(cl => cl.id === active.id);
    const newIndex = regularLists.findIndex(cl => cl.id === over.id);
    if (oldIndex === -1 || newIndex === -1) return;

    const reordered = arrayMove(regularLists, oldIndex, newIndex);

    // Optimistic update: immediately reflect new order in state
    setChoreLists(prev =>
      prev.map(cl => {
        const idx = reordered.findIndex(r => r.id === cl.id);
        return idx !== -1 ? { ...cl, sortOrder: idx } : cl;
      })
    );

    try {
      const orderedIds = reordered.map(cl => cl.id);
      await choresAPI.updateChoreListOrder(selectedFamily.familyId, childId, orderedIds);
    } catch (err) {
      console.error('Failed to update chore list order:', err);
      // Revert on failure by reloading
      loadFamilyData(selectedFamily.familyId);
      setError('Failed to update order. Please try again.');
    }
  };

  const handleToggleItem = async (choreList: ChoreList, itemId: number, isCompleted: boolean) => {
    try {
      if (isCompleted) {
        await choresAPI.unmarkItemComplete(choreList.id, itemId);
      } else {
        const result = await choresAPI.markItemComplete(choreList.id, itemId);

        // Check if star was awarded
        if (result.starAwarded && result.choreListTitle) {
          setHighlightedListId(result.choreListId ?? null);
          setTimeout(() => setHighlightedListId(null), 2000); // Clear after 2s

          celebrate({
            message: `Great job! ${result.choreListTitle} complete - you earned a star!`,
            onToast: setCelebrationMessage
          });
        }
      }
      // Reload to get updated status
      if (selectedFamily) {
        loadFamilyData(selectedFamily.familyId);
      }
    } catch (err) {
      console.error('Failed to toggle item:', err);
    }
  };

  const handleSkipToday = async (choreList: ChoreList) => {
    try {
      await choresAPI.skipChoreListToday(choreList.id);
      setSkipMessage(`"${choreList.title}" skipped for today.`);
      // Optimistic update — mark as skipped in local state
      setChoreLists(prev => prev.map(cl =>
        cl.id === choreList.id ? { ...cl, isSkippedToday: true } : cl
      ));
    } catch (err) {
      console.error('Failed to skip chore list:', err);
      setError('Failed to skip. Please try again.');
    }
  };

  const handleUnskipToday = async (choreList: ChoreList) => {
    try {
      await choresAPI.unskipChoreListToday(choreList.id);
      setSkipMessage(`"${choreList.title}" restored for today.`);
      // Optimistic update — clear skipped state
      setChoreLists(prev => prev.map(cl =>
        cl.id === choreList.id ? { ...cl, isSkippedToday: false } : cl
      ));
    } catch (err) {
      console.error('Failed to restore chore list:', err);
      setError('Failed to restore. Please try again.');
    }
  };

  const handleRetireClick = (choreList: ChoreList) => {
    setRetireTarget(choreList);
    setRetireDialogOpen(true);
  };

  const handleConfirmRetire = async () => {
    if (!retireTarget || !selectedFamily) return;

    try {
      await choresAPI.retireChoreList(retireTarget.id);
      setRetireDialogOpen(false);
      setRetireTarget(null);
      loadFamilyData(selectedFamily.familyId);
    } catch (err) {
      console.error('Failed to retire chore list:', err);
      setError('Failed to retire. Please try again.');
    }
  };

  // getRepeatLabel is defined at module level below

  const getChoreListsForChild = useCallback(
    (childId: number) => {
      // Only show Regular chore lists — MakeItRight and LetsReset have their own dedicated tabs
      const filtered = choreLists.filter(
        (cl) => cl.childId === childId && cl.type === ChoreListType.Regular && !isRetired(cl, todayStr)
      );

      // Regular lists sorted by sortOrder (parent-controlled ordering)
      return filtered.slice().sort((a, b) => a.sortOrder - b.sortOrder);
    },
    [choreLists, todayStr]
  );

  // Renders the chore lists for a single child — used in both embedded and full-page accordion views
  function renderChoreListsForChild(child: Child) {
    const childChoreLists = getChoreListsForChild(getChildId(child));
    const listIds = childChoreLists.map(cl => cl.id);

    return (
      <>
        <Box mb={2}>
          <Button
            variant="outlined"
            size="small"
            startIcon={<AddIcon />}
            onClick={() => handleOpenCreateModal(getChildId(child))}
          >
            Add Responsibilities
          </Button>
        </Box>
        {childChoreLists.length === 0 ? (
          <Typography color="text.secondary" sx={{ fontStyle: 'italic' }}>
            No responsibilities yet. Create one to get started!
          </Typography>
        ) : (
          <DndContext
            sensors={sensors}
            collisionDetection={closestCenter}
            onDragEnd={(event) => handleChoreListDragEnd(event, getChildId(child))}
          >
            <SortableContext items={listIds} strategy={verticalListSortingStrategy}>
              <Box display="flex" flexDirection="column" gap={2}>
                {childChoreLists.map((choreList) => (
                  <SortableChoreListCard
                    key={choreList.id}
                    choreList={choreList}
                    highlightedListId={highlightedListId}
                    todayDayBit={todayDayBit}
                    onEdit={handleOpenEditModal}
                    onCopy={handleOpenCopyModal}
                    onToggleItem={handleToggleItem}
                    onSkip={handleSkipToday}
                    onUnskip={handleUnskipToday}
                    onRetire={handleRetireClick}
                  />
                ))}
              </Box>
            </SortableContext>
          </DndContext>
        )}
      </>
    );
  }

  // Wraps content in the full-page layout (AppBar + Container) or a bare fragment when embedded
  const wrapPage = (content: React.ReactNode) => {
    if (embedded) return <>{content}</>;
    return (
      <Box sx={{ display: 'flex', flexDirection: 'column', minHeight: '100vh' }}>
        <SharedAppBar title="Responsibilities" />
        <Container maxWidth="lg" sx={{ py: 4 }}>
          {content}
        </Container>
      </Box>
    );
  };

  if (isLoading && families.length === 0) {
    return wrapPage(
      <Box display="flex" justifyContent="center" alignItems="center" minHeight="200px">
        <CircularProgress />
      </Box>
    );
  }

  if (error && families.length === 0) {
    return wrapPage(<Alert severity="error">{error}</Alert>);
  }

  if (families.length === 0) {
    return wrapPage(
      <Alert severity="info">
        You need to create a family and add children before you can manage responsibilities.
      </Alert>
    );
  }

  return wrapPage(
    <>
      {/* Header — hidden when embedded in a tab (parent provides context) */}
      {!embedded && (
      <Box display="flex" justifyContent="space-between" alignItems="center" mb={3}>
        <Typography variant="h4" component="h1">
          Responsibilities
        </Typography>
        <Button
          variant="contained"
          startIcon={<AddIcon />}
          onClick={() => handleOpenCreateModal()}
          disabled={children.length === 0}
        >
          New Responsibilities
        </Button>
      </Box>
      )}

      {error && (
        <Alert severity="error" sx={{ mb: 2 }}>
          {error}
        </Alert>
      )}

      {displayChildren.length === 0 ? (
        <Alert severity="info">
          Add children to your family to start creating responsibilities.
        </Alert>
      ) : embedded ? (
        /* Embedded mode (child profile tab): no accordion, render lists directly */
        displayChildren.map((child) => (
          <Box key={getChildId(child)}>
            {renderChoreListsForChild(child)}
          </Box>
        ))
      ) : (
        /* Full-page mode: accordion per child */
        displayChildren.map((child) => (
          <Accordion
            key={getChildId(child)}
            expanded={expandedChild === getChildId(child)}
            onChange={(_, isExpanded) => setExpandedChild(isExpanded ? getChildId(child) : null)}
            sx={{ mb: 1 }}
          >
            <AccordionSummary expandIcon={<ExpandMoreIcon />}>
              <Box display="flex" alignItems="center" gap={2} width="100%">
                <Typography variant="h6">{child.name}</Typography>
                <Chip
                  label={(() => {
                    const count = getChoreListsForChild(getChildId(child)).length;
                    return `${count} ${count === 1 ? 'list' : 'lists'}`;
                  })()}
                  size="small"
                  variant="outlined"
                />
              </Box>
            </AccordionSummary>
            <AccordionDetails>
              {renderChoreListsForChild(child)}
            </AccordionDetails>
          </Accordion>
        ))
      )}

      {/* Create/Edit/Copy Modal */}
      <Dialog open={modalOpen} onClose={handleCloseModal} maxWidth="sm" fullWidth>
        <DialogTitle>
          {editingChoreList ? 'Edit Responsibilities' : isCopying ? 'Copy Responsibilities' : 'Create Responsibilities'}
        </DialogTitle>
        <DialogContent>
          <Box display="flex" flexDirection="column" gap={2} mt={1}>
            {formErrors.submit && (
              <Alert severity="error">{formErrors.submit}</Alert>
            )}

            <TextField
              label="Title"
              value={formData.title}
              onChange={(e) => {
                setFormData({ ...formData, title: e.target.value });
                // Clear error when typing (Issue #1372)
                if (formErrors.title) {
                  setFormErrors({ ...formErrors, title: '' });
                }
              }}
              onBlur={() => {
                // Validate on blur for real-time feedback (Issue #1372)
                if (formData.title.trim() && formData.childId) {
                  const normalizedTitle = formData.title.trim().toLowerCase();
                  const childLists = choreLists.filter(cl => cl.childId === formData.childId);
                  const duplicate = childLists.find(cl =>
                    cl.title.toLowerCase() === normalizedTitle &&
                    cl.type === ChoreListType.Regular &&
                    (!editingChoreList || cl.id !== editingChoreList.id)
                  );

                  if (duplicate) {
                    const child = children.find(c => getChildId(c) === formData.childId);
                    setFormErrors({
                      ...formErrors,
                      title: `A responsibility list named '${formData.title.trim()}' already exists for ${child?.name || 'this child'}. Please use a different name.`
                    });
                  }
                }
              }}
              error={!!formErrors.title}
              helperText={formErrors.title}
              fullWidth
              required
            />

            <TextField
              label="Notes (optional)"
              value={formData.notes}
              onChange={(e) => setFormData({ ...formData, notes: e.target.value })}
              fullWidth
              multiline
              rows={2}
            />

            <FormControl fullWidth error={!!formErrors.childId} disabled={!!editingChoreList}>
              <InputLabel>Child</InputLabel>
              <Select
                value={formData.childId || ''}
                onChange={(e) => {
                  const newChildId = e.target.value as number;
                  setFormData({ ...formData, childId: newChildId });

                  // Clear title error when child changes (Issue #1372)
                  if (formErrors.title) {
                    setFormErrors({ ...formErrors, title: '' });
                  }
                }}
                label="Child"
              >
                {displayChildren.map((child) => (
                  <MenuItem key={getChildId(child)} value={getChildId(child)}>
                    {child.name}
                  </MenuItem>
                ))}
              </Select>
              {formErrors.childId && (
                <Typography variant="caption" color="error">
                  {formErrors.childId}
                </Typography>
              )}
              {editingChoreList && (
                <Typography variant="caption" color="text.secondary">
                  Child cannot be changed. Use Copy to create for another child.
                </Typography>
              )}
            </FormControl>

            <TextField
              label="Start Date"
              type="date"
              value={formData.startDate}
              onChange={(e) => setFormData({ ...formData, startDate: e.target.value })}
              error={!!formErrors.startDate}
              helperText={formErrors.startDate || (editingChoreList ? 'Changing start date will affect repeat schedule' : '')}
              fullWidth
              InputLabelProps={{ shrink: true }}
            />

            <FormControl fullWidth>
              <InputLabel>Repeat</InputLabel>
              <Select
                value={formData.repeatFrequency}
                onChange={(e) =>
                  setFormData({
                    ...formData,
                    repeatFrequency: e.target.value as RepeatFrequency,
                        // Reset requiredForHeart if not daily
                        requiredForHeart:
                          e.target.value === 'Daily' ? formData.requiredForHeart : false,
                      })
                    }
                    label="Repeat"
                  >
                    <MenuItem value="Daily">Daily</MenuItem>
                    <MenuItem value="Weekly">Weekly</MenuItem>
                    <MenuItem value="Monthly">Monthly</MenuItem>
                  </Select>
                </FormControl>

            {formData.repeatFrequency === 'Daily' && (
              <Box>
                <FormLabel component="legend" sx={{ mb: 1, fontSize: '0.875rem' }}>
                  Active days
                </FormLabel>
                <Box display="flex" gap={0.5}>
                  {DAYS_OF_WEEK.map(({ label, bit }) => (
                    <FormControlLabel
                      key={label}
                      control={
                        <Checkbox
                          checked={(formData.daysOfWeek & bit) !== 0}
                          onChange={(e) => {
                            const newDays = e.target.checked
                              ? formData.daysOfWeek | bit
                              : formData.daysOfWeek & ~bit;
                            setFormData({ ...formData, daysOfWeek: newDays });
                          }}
                          size="small"
                        />
                      }
                      label={label}
                      labelPlacement="bottom"
                      sx={{ mx: 0, '& .MuiFormControlLabel-label': { fontSize: '0.7rem' } }}
                    />
                  ))}
                </Box>
                {formErrors.daysOfWeek && (
                  <Typography variant="caption" color="error">
                    {formErrors.daysOfWeek}
                  </Typography>
                )}
              </Box>
            )}

            <FormControlLabel
              control={
                <Switch
                  checked={formData.requiredForHeart}
                  onChange={(e) =>
                    setFormData({ ...formData, requiredForHeart: e.target.checked })
                  }
                  disabled={formData.repeatFrequency !== 'Daily'}
                />
              }
              label={
                <Box>
                  <Typography>Required for heart</Typography>
                  {formData.repeatFrequency !== 'Daily' && (
                    <Typography variant="caption" color="text.secondary">
                      Only available for daily responsibilities
                    </Typography>
                  )}
                </Box>
              }
            />

            <FormControlLabel
              control={
                <Switch
                  checked={formData.requiresApproval}
                  onChange={(e) =>
                    setFormData({ ...formData, requiresApproval: e.target.checked })
                  }
                />
              }
              label="Requires parent approval when complete"
            />

            <Divider />

            <Typography variant="subtitle1">Checklist Items</Typography>
            {formErrors.items && (
              <Typography variant="caption" color="error">
                {formErrors.items}
              </Typography>
            )}

            <Box display="flex" gap={1}>
              <TextField
                label="New item"
                value={newItemTitle}
                onChange={(e) => setNewItemTitle(e.target.value)}
                onKeyPress={(e) => e.key === 'Enter' && handleAddItem()}
                fullWidth
                size="small"
              />
              <Button variant="outlined" onClick={handleAddItem}>
                Add
              </Button>
            </Box>

            {formData.items.length === 0 ? (
              <Typography variant="body2" color="text.secondary" sx={{ fontStyle: 'italic', py: 2 }}>
                No items yet. Add items above to create your checklist.
              </Typography>
            ) : (
              <DndContext
                sensors={sensors}
                collisionDetection={closestCenter}
                onDragEnd={handleDragEnd}
              >
                <SortableContext
                  items={formData.items.map((item) => item.tempId)}
                  strategy={verticalListSortingStrategy}
                >
                  <List dense disablePadding>
                    {formData.items.map((item, index) => (
                      <SortableItem
                        key={item.tempId}
                        id={item.tempId}
                        title={item.title}
                        onRemove={() => handleRemoveItem(index)}
                      />
                    ))}
                  </List>
                </SortableContext>
              </DndContext>
            )}
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCloseModal} disabled={isSaving}>
            Cancel
          </Button>
          <Button onClick={handleSave} variant="contained" disabled={isSaving}>
            {isSaving ? <CircularProgress size={24} /> : editingChoreList ? 'Save' : 'Create'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Retire Confirmation Dialog */}
      <Dialog open={retireDialogOpen} onClose={() => setRetireDialogOpen(false)}>
        <DialogTitle>Retire Responsibilities?</DialogTitle>
        <DialogContent>
          <Typography>
            Retire &ldquo;{retireTarget?.title}&rdquo;? It will no longer appear on the Responsibilities page.
            Past hearts earned with this list are preserved.
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setRetireDialogOpen(false)}>Cancel</Button>
          <Button onClick={handleConfirmRetire} variant="contained">
            Retire
          </Button>
        </DialogActions>
      </Dialog>

      {/* Celebration Toast */}
      <Snackbar
        open={!!celebrationMessage}
        autoHideDuration={4000}
        onClose={() => setCelebrationMessage(null)}
        anchorOrigin={{ vertical: 'top', horizontal: 'center' }}
      >
        <Alert
          onClose={() => setCelebrationMessage(null)}
          severity="success"
          sx={{ width: '100%', fontSize: '1.1rem' }}
        >
          {celebrationMessage}
        </Alert>
      </Snackbar>

      {/* Skip Today Toast */}
      <Snackbar
        open={!!skipMessage}
        autoHideDuration={3000}
        onClose={() => setSkipMessage(null)}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
      >
        <Alert onClose={() => setSkipMessage(null)} severity="info" sx={{ width: '100%' }}>
          {skipMessage}
        </Alert>
      </Snackbar>
    </>
  );
};

export default ChoresPage;
